import { useCallback, useEffect, useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useDispatch, useSelector } from 'react-redux';
import { differenceInCalendarDays, format, parseISO, subDays } from 'date-fns';

import { SplashScreen } from 'src/components/loading-screen';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { CountryBar } from 'src/components/country-bar';
import { CountryItem } from 'src/components/country-bar/types';
import { useCountryData } from 'src/components/visibility/hooks/useCountryData';
import { HEADER } from 'src/layouts/config-layout';
import {
  setDatesForcedByActivation,
  setFilterCompare,
  setFilterCountry,
  setFilterDate,
  setFilterDateCompare,
  setFilterMinDatePicker,
  setFilterTimestep,
  setSearchPerfDateOverridden,
} from 'src/context/reducers/filter-settings/filterSlice';
import { getDateRangeIso, getLast12WeeksDateRange } from 'src/utils/format-time';
import { restoreSearchPerformanceDateRange } from 'src/utils/searchPerformanceDateMemory';

import { CountryTabLabel } from '../explore/CountryTabLabel';
import { usePersistedCountries } from '../explore/usePersistedCountries';
import { useFunnelKpis } from './hooks/useFunnelKpis';
import FunnelDistributionChart from './FunnelDistributionChart';
import FunnelPerformanceChart from './FunnelPerformanceChart';
import { PAGE_PADDING_X, SECTION_GAP } from './constants';
import KpiToggle, { useKPIToggle } from '../explore/ExploreKpiCards';
import ExploreTable from '../explore/ExploreTable';

export default function FunnelView() {
  const dispatch = useDispatch();
  const filterStore = useSelector((state: any) => state.filters);
  const workspace: any = useCurrentWorkspace();

  const [selectedDisplayGraph, setSelectedDisplayGraphValue] = useState('Value');
  const [tableEverLoaded, setTableEverLoaded] = useState(false);

  const toggle = useKPIToggle(
    filterStore.extraOptions?.mode === 'activations'
      ? ['ad_brand_traffic', 'cpc']
      : ['brand_searches', 'total_brand_traffic']
  );

  const { countriesData: countries, isLoading: isLoadingCountries } = useCountryData();

  const [countryList, setCountryList] = useState<CountryItem[]>([]);

  const loadingTop =
    toggle.isLoadingStats ||
    toggle.isLoadingCompareStats ||
    !toggle.statsData;

  useEffect(() => {
    if (countryList.length === 0 && countries.length > 0) {
      setCountryList(countries);
    }
  }, [countries, countryList.length]);

  const handleRestore = useCallback(
    (names: string[]) => {
      dispatch(setFilterCountry(names));
    },
    [dispatch]
  );

  const { persist } = usePersistedCountries(countryList, handleRestore);

  const handleCountriesChange = (names: string[]) => {
    dispatch(setFilterCountry(names));
    persist(names);
  };

  const allCountryNames = countryList.map((country) => country.country_name);

  const {
    mainData: kpisData,
    compareData,
    error: kpisError,
    isFetching: isFetchingKpis,
  } = useFunnelKpis(
    filterStore?.countries ?? [],
    allCountryNames,
    workspace
  );

  useEffect(() => {
    if (kpisData?.min_date) {
      dispatch(setFilterMinDatePicker(kpisData.min_date));
    }
  }, [kpisData?.min_date, dispatch]);

  useEffect(() => {
    if (!workspace?.id) return;

    if (restoreSearchPerformanceDateRange(dispatch, workspace.id)) {
      return;
    }

    dispatch(setSearchPerfDateOverridden(false));
    dispatch(setDatesForcedByActivation(false));

    if (workspace?.name?.includes('Demo')) {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const { start, end } = getDateRangeIso(
        new Date(2024, 8, 1),
        new Date(2024, 10, 30),
        timezone
      );

      dispatch(
        setFilterTimestep({
          timestep: 'date_day',
          skipAlignment: true,
        })
      );

      dispatch(
        setFilterDate({
          start,
          end,
          skipAlignment: true,
        })
      );

      return;
    }

    const { start, end } = getLast12WeeksDateRange();
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    const periodDuration =
      differenceInCalendarDays(endDate, startDate) + 1;
    const compareEnd = subDays(startDate, 1);
    const compareStart = subDays(compareEnd, periodDuration - 1);

    dispatch(
      setFilterTimestep({
        timestep: 'year_week',
        skipAlignment: true,
      })
    );

    dispatch(
      setFilterDate({
        start: format(startDate, 'yyyy-MM-dd'),
        end: format(endDate, 'yyyy-MM-dd'),
        skipAlignment: true,
      })
    );

    dispatch(
      setFilterDateCompare({
        compareStart: format(compareStart, 'yyyy-MM-dd'),
        compareEnd: format(compareEnd, 'yyyy-MM-dd'),
        skipAlignment: true,
      })
    );

    dispatch(setFilterCompare(true));
  }, [dispatch, workspace?.id, workspace?.name]);


  useEffect(() => {
    if (!loadingTop) {
      setTableEverLoaded(true);
    }
  }, [loadingTop]);

  if (
    !filterStore?.countries ||
    !filterStore?.keywords ||
    !filterStore?.devices
  ) {
    return <SplashScreen />;
  }

  return (
    <Box px="24px">
      <CountryBar
        countries={countryList}
        multiple
        selectedCountries={filterStore.countries ?? []}
        onCountriesChange={handleCountriesChange}
        loading={
          isLoadingCountries ||
          (countryList.length === 0 && isFetchingKpis)
        }
        stickyTop={HEADER.H_DESKTOP * 3 + 6}
        backgroundColor="background.paper"
        topGap={60}
        reserveGapWhenHidden
        contentPaddingX={PAGE_PADDING_X}
        tabSpacing={12}
        renderCountryLabel={(country, isSelected) => (
          <CountryTabLabel
            country={country}
            isSelected={isSelected}
          />
        )}
      />

      {loadingTop ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr)) 40px',
            gap: '4px',
            width: '100%',
            mb: '24px',
          }}
        >
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={index}
              width="100%"
              height={100}
              variant="rectangular"
              animation="wave"
              sx={{
                borderRadius: 0,
                borderTopLeftRadius: index === 0 ? '12px' : 0,
                borderBottomLeftRadius: index === 0 ? '12px' : 0,
              }}
            />
          ))}

          <Skeleton
            width="100%"
            height={100}
            variant="rectangular"
            animation="wave"
            sx={{
              borderTopRightRadius: '12px',
              borderBottomRightRadius: '12px',
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            mb: '24px',
          }}
        >
          <KpiToggle {...toggle} />
        </Box>
      )}

      <Box sx={{ marginBottom: '24px' }}>
        <Grid container spacing={SECTION_GAP}>
          <Grid xs={12} md={6}>
            <FunnelDistributionChart
              kpisData={kpisData}
              compareData={compareData}
              kpisError={kpisError}
              isFetchingKpis={isFetchingKpis}
              selectedDisplayGraph={selectedDisplayGraph}
              setSelectedDisplayGraphValue={setSelectedDisplayGraphValue}
            />
          </Grid>

          <Grid xs={12} md={6}>
            <FunnelPerformanceChart
              kpisData={kpisData}
              compareData={compareData}
              kpisError={kpisError}
              isFetchingKpis={isFetchingKpis}
            />
          </Grid>
        </Grid>
      </Box>

      {loadingTop && !tableEverLoaded ? (
        <Skeleton
          variant="rectangular"
          animation="wave"
          width="100%"
          height={403}
          sx={{
            borderRadius: 2,
            mt: 2,
          }}
        />
      ) : (
        <ExploreTable />
      )}
    </Box>
  );
}